import type { PlanBundleInvitedVendor, PlanBundleTeamMember } from '../types';

// Newest-first ordering for a plan's feature list, shared by the editor
// panel and both read-only viewers (DefensePlanPanel.web.tsx,
// DefensePlanViewer.web.tsx, DefensePlanViewerCard.web.tsx) so the three
// surfaces read the same list the same way. Display-only: the stored
// `DefensePlan.features` array order is never touched (undo, the "who's
// responsible" links and every id lookup key off array position or id, not
// display order).

// Duck-typed rather than importing DefensePlanFeature: any feature-shaped
// value with a Firestore-Timestamp-like createdAt works, which keeps this
// usable from a plain object in a test without needing a real Timestamp.
interface TimestampLike {
  toMillis(): number;
}
interface HasCreatedAt {
  createdAt?: TimestampLike | null;
}

/**
 * Returns a new array of features ordered newest first by `createdAt`.
 * A tie (equal timestamps) or a missing/invalid `createdAt` on either side
 * falls back to reverse array index, so a feature drawn later (further
 * along the stored array) still sorts ahead of one drawn earlier. Never
 * mutates `features`.
 */
export function orderFeaturesNewestFirst<T extends HasCreatedAt>(features: T[]): T[] {
  return features
    .map((feature, index) => ({ feature, index }))
    .sort((a, b) => {
      const aMillis = a.feature.createdAt?.toMillis() ?? null;
      const bMillis = b.feature.createdAt?.toMillis() ?? null;
      if (aMillis !== null && bMillis !== null && aMillis !== bMillis) {
        return bMillis - aMillis;
      }
      return b.index - a.index;
    })
    .map(({ feature }) => feature);
}

// ----- bundles (Sept 16 2026 — Glenn's "bundle steps" ask) -----------------
//
// Pure helpers shared by the store (defensePlansStore's createBundle/
// renameBundle/ungroupBundle/setBundleMembers) and every list that renders a
// plan's features grouped by bundle (the editor panel, the desktop viewer,
// the phone card). No Firestore, no zustand — the store applies these to an
// in-memory plan, then writes the result.

interface HasBundleId {
  id: string;
  bundleId?: string;
}

interface BundleLike extends HasCreatedAt {
  id: string;
  name: string;
}

/**
 * Reassigns `featureIds` onto `bundleId`, and clears `bundleId` off any
 * OTHER feature that currently carries it but isn't in the new set — so a
 * feature moving into a bundle always leaves whichever bundle it was in
 * before (a feature belongs to at most one bundle at a time), and a bundle
 * whose membership is being replaced (setBundleMembers, or ungroupBundle
 * calling this with an empty `featureIds`) drops every id no longer listed.
 * Never mutates `features`; a feature whose membership doesn't change comes
 * back as the same reference.
 */
export function applyBundleMembership<T extends HasBundleId>(
  features: T[],
  bundleId: string,
  featureIds: string[]
): T[] {
  const target = new Set(featureIds);
  return features.map((feature) => {
    if (target.has(feature.id)) {
      return feature.bundleId === bundleId ? feature : { ...feature, bundleId };
    }
    if (feature.bundleId === bundleId) {
      const { bundleId: _drop, ...rest } = feature;
      return rest as T;
    }
    return feature;
  });
}

/**
 * Moves `featureIds` into `bundleId`, or out of whatever bundle they are in
 * when `bundleId` is null (a loose feature stays loose). Unlike
 * applyBundleMembership this never replaces a bundle's membership: members
 * of the target that aren't listed stay put. Ids that match no feature are
 * ignored. A bundle left with no members is pruned, unless `keepIds` names it
 * (see pruneEmptyBundles). Never mutates its
 * inputs; a feature whose membership doesn't change comes back as the same
 * reference, and when nothing changes at all both returned arrays are the
 * same references that went in, so a caller can detect a no-op with `===`.
 */
export function moveToBundle<F extends HasBundleId, B extends { id: string }>(
  features: F[],
  bundles: B[],
  featureIds: string[],
  bundleId: string | null,
  keepIds?: ReadonlySet<string>
): { features: F[]; bundles: B[] } {
  const target = new Set(featureIds);
  let changed = false;
  const nextFeatures = features.map((feature) => {
    if (!target.has(feature.id)) return feature;
    if (bundleId === null) {
      if (feature.bundleId === undefined) return feature;
      changed = true;
      const { bundleId: _drop, ...rest } = feature;
      return rest as F;
    }
    if (feature.bundleId === bundleId) return feature;
    changed = true;
    return { ...feature, bundleId };
  });
  if (!changed) return { features, bundles };
  return { features: nextFeatures, bundles: pruneEmptyBundles(bundles, nextFeatures, keepIds) };
}

/**
 * Drops every bundle no feature currently references — a bundle emptied by
 * a feature delete, an ungroup, or a setBundleMembers call that removed its
 * last member. Order of the surviving bundles is preserved.
 *
 * `keepIds` (Oct 2026) are bundles to keep even with no members: the ones
 * that still have an upcoming event. An event links to its bundle by id, so
 * pruning the bundle would leave the event pointing at nothing while people
 * are signed up for it. The caller builds the set from the events store
 * (bundleIdsKeptByEvents) and this stays pure. Unbundling on purpose does not
 * go through here: it removes the bundle itself, after cancelling its events.
 */
/**
 * Bundles that bids keep alive with no members (Oct 2026): any bundle open
 * for bids, and any bundle a vendor has bid on. Pruning one would strand a
 * vendor's bid, or an open public link, on a bundle that no longer exists.
 * OR it with the events set at every pruneEmptyBundles call.
 */
export function bundleIdsKeptByBids(
  bundles: { id: string; bidsOpen?: boolean }[],
  bids: { bundleId: string }[]
): Set<string> {
  const keep = new Set<string>();
  for (const bundle of bundles) if (bundle.bidsOpen) keep.add(bundle.id);
  for (const bid of bids) keep.add(bid.bundleId);
  return keep;
}

export function pruneEmptyBundles<B extends { id: string }>(
  bundles: B[],
  features: { bundleId?: string }[],
  keepIds?: ReadonlySet<string>
): B[] {
  const used = new Set<string>();
  for (const feature of features) {
    if (feature.bundleId) used.add(feature.bundleId);
  }
  return bundles.filter((bundle) => used.has(bundle.id) || !!keepIds?.has(bundle.id));
}

// Generic over the bundle type too (not just fixed to BundleLike) so a
// caller passing real PlanBundle objects gets real PlanBundle objects back
// out — DefensePlanBundleRow expects the full shape (id, name, createdAt),
// not just the fields this file's own math needs.
export type PlanListEntry<F, B extends BundleLike = BundleLike> =
  | { type: 'bundle'; bundle: B; members: F[] }
  | { type: 'feature'; feature: F };

/**
 * The editor/viewer feature list, in display order: every bundle that has
 * at least one member (bundles with none are assumed already pruned — see
 * pruneEmptyBundles — but an empty one here is simply skipped rather than
 * shown with nothing under it), newest-first by its most recent member's
 * `createdAt` (falling back to the bundle's own `createdAt` for a bundle
 * whose members somehow carry no timestamp), each with its own members
 * newest-first; then every unbundled feature, newest-first. Never mutates
 * either input.
 */
export function groupFeaturesForList<F extends HasBundleId & HasCreatedAt, B extends BundleLike>(
  features: F[],
  bundles: B[],
  // Bundles to show even with no members: the editor passes the ones an
  // upcoming event keeps alive (pruneEmptyBundles' keepIds), so the owner can
  // still see them and drag defenses back in.
  keepEmptyIds?: ReadonlySet<string>
): PlanListEntry<F, B>[] {
  const byBundle = new Map<string, F[]>();
  const unbundled: F[] = [];
  for (const feature of features) {
    if (feature.bundleId) {
      const existing = byBundle.get(feature.bundleId);
      if (existing) existing.push(feature);
      else byBundle.set(feature.bundleId, [feature]);
    } else {
      unbundled.push(feature);
    }
  }

  const groups = bundles
    .map((bundle) => ({
      bundle,
      members: orderFeaturesNewestFirst(byBundle.get(bundle.id) ?? []),
    }))
    .filter((group) => group.members.length > 0 || !!keepEmptyIds?.has(group.bundle.id));

  groups.sort((a, b) => {
    const aMillis = a.members[0]?.createdAt?.toMillis() ?? a.bundle.createdAt?.toMillis() ?? 0;
    const bMillis = b.members[0]?.createdAt?.toMillis() ?? b.bundle.createdAt?.toMillis() ?? 0;
    return bMillis - aMillis;
  });

  const entries: PlanListEntry<F, B>[] = groups.map((group) => ({
    type: 'bundle' as const,
    bundle: group.bundle,
    members: group.members,
  }));
  for (const feature of orderFeaturesNewestFirst(unbundled)) {
    entries.push({ type: 'feature', feature });
  }
  return entries;
}

/**
 * Ids of the feature rows on screen, top to bottom: a bundle's members only
 * while it is expanded, then the loose features. The keyboard walks this list.
 */
export function visibleFeatureIds<F extends { id: string }, B extends BundleLike>(
  entries: PlanListEntry<F, B>[],
  isExpanded: (bundleId: string, memberCount: number) => boolean
): string[] {
  const ids: string[] = [];
  for (const entry of entries) {
    if (entry.type === 'feature') {
      ids.push(entry.feature.id);
    } else if (isExpanded(entry.bundle.id, entry.members.length)) {
      for (const member of entry.members) ids.push(member.id);
    }
  }
  return ids;
}

interface NameableFeature {
  id: string;
  strategy: string;
  structureId?: string;
}

/** Longest bundle name the schema accepts (PlanBundleSchema.name). */
const BUNDLE_NAME_MAX = 60;

/**
 * The name a bundle gets when the owner leaves the name field blank
 * (Sept 24 2026: Glenn didn't expect a name to be required). The panel
 * shows it as the field's placeholder, so the owner sees it before saving.
 *
 * One strategy across the picks: "Sprinklers, 3 homes" when every pick is
 * a home, "Fire break, 3 defenses" otherwise (not "Fire break (3)", which
 * reads like the row label "Fire break 3"). Mixed strategies: the first-ticked
 * strategy plus a count of the rest, "Sprinklers + 2 more". A name already
 * used by another bundle in the plan gets " 2", " 3", ... so two bundles
 * never read the same. `selectedIds` is in tick order; `labelFor` maps a
 * strategy key to its display label (kept as a parameter so this file
 * stays free of the strategy table).
 */
export function defaultBundleName(
  selectedIds: string[],
  features: NameableFeature[],
  existingNames: string[],
  labelFor: (strategy: string) => string
): string {
  const byId = new Map(features.map((f) => [f.id, f]));
  const picked = selectedIds.map((id) => byId.get(id)).filter((f): f is NameableFeature => !!f);
  if (picked.length === 0) return '';
  const strategies = Array.from(new Set(picked.map((f) => f.strategy)));
  const first = labelFor(strategies[0]);
  let base: string;
  if (strategies.length === 1) {
    const allHomes = picked.every((f) => !!f.structureId);
    const n = picked.length;
    const noun = allHomes ? (n === 1 ? 'home' : 'homes') : n === 1 ? 'defense' : 'defenses';
    base = `${first}, ${n} ${noun}`;
  } else {
    base = `${first} + ${picked.length - 1} more`;
  }
  const taken = new Set(existingNames.map((n) => n.trim().toLowerCase()));
  const fit = (name: string, suffix = '') =>
    name.slice(0, BUNDLE_NAME_MAX - suffix.length).trimEnd() + suffix;
  if (!taken.has(fit(base).toLowerCase())) return fit(base);
  for (let i = 2; ; i++) {
    const candidate = fit(base, ` ${i}`);
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

// ----- bundle team and leader (Oct 5 2026 — Glenn: "assign more than one ----
// ----- person to a bundle and assign a leader") ------------------------------
//
// A bundle owns a team of responsible parties and optionally names one of
// them leader. Assignments stay one per (defense, party) because status, the
// update loop, emails and the CSV all read them; the team says WHO, and the
// store keeps the per-defense assignments in step with it. Everything here is
// pure so jest can cover each case without Firestore.

interface TeamAssignment {
  id: string;
  featureId: string;
  partyId: string;
  description?: string;
}

interface TeamBundle extends BundleLike {
  team?: PlanBundleTeamMember[];
  leaderPartyId?: string;
}

/**
 * The bundle's team. A stored `team` wins, including an empty one (the owner
 * removed everybody on purpose). A bundle with no stored `team` has never had
 * its team edited, which covers every bundle created before teams existed, so
 * the team is derived: the parties linked to EVERY member defense. "Every"
 * rather than "any", because a party on just one member is that defense's own
 * person, not the bundle's. Derived entries carry the description from the
 * first member's link. Empty bundles derive nothing.
 */
export function effectiveBundleTeam(
  bundle: Pick<TeamBundle, 'team'>,
  members: { id: string }[],
  assignments: Record<string, TeamAssignment>
): PlanBundleTeamMember[] {
  if (bundle.team) return bundle.team;
  if (members.length === 0) return [];
  const links = Object.values(assignments);
  const memberIds = new Set(members.map((m) => m.id));
  // featureId -> partyIds, over member defenses only
  const byFeature = new Map<string, Map<string, TeamAssignment>>();
  for (const link of links) {
    if (!memberIds.has(link.featureId)) continue;
    const forFeature = byFeature.get(link.featureId) ?? new Map<string, TeamAssignment>();
    forFeature.set(link.partyId, link);
    byFeature.set(link.featureId, forFeature);
  }
  const first = byFeature.get(members[0].id);
  if (!first) return [];
  const team: PlanBundleTeamMember[] = [];
  for (const [partyId, link] of first) {
    if (!members.every((m) => byFeature.get(m.id)?.has(partyId))) continue;
    team.push(link.description ? { partyId, description: link.description } : { partyId });
  }
  return team;
}

/** The leader's party id, or undefined when none is set or the id is not on `team`. */
export function effectiveBundleLeader(
  bundle: Pick<TeamBundle, 'leaderPartyId'>,
  team: PlanBundleTeamMember[]
): string | undefined {
  const id = bundle.leaderPartyId;
  return id && team.some((t) => t.partyId === id) ? id : undefined;
}

/**
 * A copy of `bundle` carrying `team` and `leaderPartyId`. The leader key is
 * omitted, never '' or undefined, when there is no leader (Firestore rejects
 * undefined and a '' would read as a party id); the same rule as applyBundleMembership's
 * `bundleId` drop. A leader not on `team` is dropped, since the leader must be
 * a teammate.
 */
export function withBundleTeam<B extends TeamBundle>(
  bundle: B,
  team: PlanBundleTeamMember[],
  leaderPartyId?: string
): B {
  const { leaderPartyId: _oldLeader, ...rest } = bundle;
  const next = { ...rest, team } as B;
  if (leaderPartyId && team.some((t) => t.partyId === leaderPartyId)) {
    next.leaderPartyId = leaderPartyId;
  }
  return next;
}

interface PlanLike {
  features: HasBundleId[];
  bundles: TeamBundle[];
}

export interface TeamReconcile {
  toCreate: { featureId: string; partyId: string; description?: string }[];
  /** Assignment doc ids. */
  toDelete: string[];
}

/**
 * Which assignments must change so a plan's defenses carry their bundle's
 * team after a membership change. For every defense whose `bundleId` differs
 * between `prevPlan` and `nextPlan`:
 * - links to the OLD bundle's team parties are deleted, unless that party is
 *   also on the new team (it keeps its link);
 * - links for the NEW team's parties are created (status `asked`, the
 *   teammate's description) where none exists.
 * Links to parties who are not on either team are never touched, so a person
 * assigned to one defense directly stays put.
 *
 * Two deliberate choices. A bundle's team is read from the PREVIOUS plan when
 * the next bundle has no stored team, because deriving it from the new
 * membership would count the newcomer, who has no links yet, and come out
 * empty. And when the old bundle no longer exists in `nextPlan` (ungrouped,
 * or emptied by the change) nothing is deleted: ungroup keeps assignments, so
 * undo can bring the team back intact.
 */
export function reconcileTeamAssignments(
  prevPlan: PlanLike,
  nextPlan: PlanLike,
  assignments: Record<string, TeamAssignment>
): TeamReconcile {
  const prevById = new Map(prevPlan.features.map((f) => [f.id, f]));
  const linkList = Object.values(assignments);
  const existing = new Set(linkList.map((a) => `${a.featureId}\u0000${a.partyId}`));
  const prevMembersOf = (bundleId: string) =>
    prevPlan.features.filter((f) => f.bundleId === bundleId);

  const teamCache = new Map<string, PlanBundleTeamMember[]>();
  const teamOf = (bundleId: string | undefined): PlanBundleTeamMember[] => {
    if (!bundleId) return [];
    const cached = teamCache.get(bundleId);
    if (cached) return cached;
    const next = nextPlan.bundles.find((b) => b.id === bundleId);
    const prev = prevPlan.bundles.find((b) => b.id === bundleId);
    const team = next?.team
      ? next.team
      : prev
        ? effectiveBundleTeam(prev, prevMembersOf(bundleId), assignments)
        : [];
    teamCache.set(bundleId, team);
    return team;
  };

  const toCreate: TeamReconcile['toCreate'] = [];
  const toDelete = new Set<string>();
  for (const feature of nextPlan.features) {
    const prevFeature = prevById.get(feature.id);
    const oldId = prevFeature?.bundleId;
    const newId = feature.bundleId;
    if (oldId === newId) continue;
    const newTeam = teamOf(newId);
    const newParties = new Set(newTeam.map((t) => t.partyId));
    if (oldId && nextPlan.bundles.some((b) => b.id === oldId)) {
      const oldParties = new Set(teamOf(oldId).map((t) => t.partyId));
      for (const link of linkList) {
        if (link.featureId !== feature.id) continue;
        if (oldParties.has(link.partyId) && !newParties.has(link.partyId)) toDelete.add(link.id);
      }
    }
    for (const member of newTeam) {
      if (existing.has(`${feature.id}\u0000${member.partyId}`)) continue;
      toCreate.push(
        member.description
          ? { featureId: feature.id, partyId: member.partyId, description: member.description }
          : { featureId: feature.id, partyId: member.partyId }
      );
    }
  }
  return { toCreate, toDelete: Array.from(toDelete) };
}

/**
 * Drops `partyId` from every bundle's team and clears a `leaderPartyId` that
 * names it. Used when a party is deleted from the roster, so the team never
 * lists an "Unknown party". Bundles that don't mention the party come back as
 * the same reference, so a caller can tell whether anything changed.
 */
export function stripPartyFromBundles<B extends TeamBundle>(bundles: B[], partyId: string): B[] {
  return bundles.map((bundle) => {
    const onTeam = bundle.team?.some((t) => t.partyId === partyId);
    const isLeader = bundle.leaderPartyId === partyId;
    if (!onTeam && !isLeader) return bundle;
    const { leaderPartyId: _drop, ...rest } = bundle;
    const next = { ...rest } as B;
    if (bundle.team) next.team = bundle.team.filter((t) => t.partyId !== partyId);
    if (bundle.leaderPartyId && !isLeader) next.leaderPartyId = bundle.leaderPartyId;
    return next;
  });
}

/**
 * A copy of `bundles` with `team` and `leaderPartyId` removed. A duplicated
 * plan has no parties yet, so the stored party ids would point at nothing.
 */
export function stripBundleTeams<B extends TeamBundle>(bundles: B[]): B[] {
  return bundles.map((bundle) => {
    const { team: _team, leaderPartyId: _leader, ...rest } = bundle;
    return rest as unknown as B;
  });
}

// ----- invited vendors (Nov 2026) ------------------------------------------

interface InvitedVendorBundle {
  invitedVendors?: PlanBundleInvitedVendor[];
}

/** The fields an owner supplies when shortlisting a vendor; invitedAt is set later. */
export type InvitedVendorEntry = Omit<PlanBundleInvitedVendor, 'invitedAt'>;

/**
 * A copy of `bundle` with the vendor shortlisted, or taken off the list when
 * `entry.vendorId` is already on it. The key stays absent until the first add,
 * and removing the last entry deletes it: Firestore rejects undefined and an
 * empty list would be noise. A new entry is dropped, not added, once `max`
 * entries are on the list. Optional keys with no value are omitted.
 */
export function withInvitedVendorToggled<B extends InvitedVendorBundle>(
  bundle: B,
  entry: InvitedVendorEntry,
  max: number
): B {
  const current = bundle.invitedVendors ?? [];
  const { invitedVendors: _drop, ...rest } = bundle;
  let next: PlanBundleInvitedVendor[];
  if (current.some((v) => v.vendorId === entry.vendorId)) {
    next = current.filter((v) => v.vendorId !== entry.vendorId);
  } else {
    if (current.length >= max) return bundle;
    const clean: PlanBundleInvitedVendor = {
      vendorId: entry.vendorId,
      organization: entry.organization,
      ...(entry.repId ? { repId: entry.repId } : {}),
      ...(entry.repName ? { repName: entry.repName } : {}),
    };
    next = [...current, clean];
  }
  return (next.length > 0 ? { ...rest, invitedVendors: next } : rest) as B;
}

/**
 * A copy of `bundle` with `at` stamped as the invitedAt of the entry for
 * `vendorId`. A bundle without that vendor comes back as the same reference.
 */
export function withVendorInvitedAt<B extends InvitedVendorBundle>(
  bundle: B,
  vendorId: string,
  at: NonNullable<PlanBundleInvitedVendor['invitedAt']>
): B {
  if (!bundle.invitedVendors?.some((v) => v.vendorId === vendorId)) return bundle;
  return {
    ...bundle,
    invitedVendors: bundle.invitedVendors.map((v) =>
      v.vendorId === vendorId ? { ...v, invitedAt: at } : v
    ),
  };
}

/**
 * A copy of `bundles` with `invitedVendors` removed. A duplicated plan starts
 * with no one asked, so the copy shouldn't claim vendors were invited.
 */
export function stripInvitedVendors<B extends InvitedVendorBundle>(bundles: B[]): B[] {
  return bundles.map((bundle) => {
    const { invitedVendors: _drop, ...rest } = bundle;
    return rest as unknown as B;
  });
}
